import { get, patch, post, put, remove } from 'src/api/requests';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import { ResponseType } from 'axios';
import { useCallback, useMemo, useRef, useLayoutEffect } from 'react';

export type AuthedRequestOptions = {
  headers?: Record<string, string>;
  responseType?: ResponseType;
};

export type AuthedRequest = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (url: string, options?: AuthedRequestOptions) => Promise<any>;
  post: (
    url: string,
    body: object,
    options?: AuthedRequestOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  patch: (
    url: string,
    body: object,
    options?: AuthedRequestOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  put: (
    url: string,
    body: object,
    options?: AuthedRequestOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remove: (url: string, options?: AuthedRequestOptions) => Promise<any>;
};

export type BaseRequestOptions = {
  envId?: string;
  withoutToken?: boolean;
  headers?: Record<string, string>;
};

const buildRequestOptions = (
  baseOptions: BaseRequestOptions,
  options?: AuthedRequestOptions,
) => ({
  ...options,
  ...baseOptions,
  headers: { ...options?.headers, ...baseOptions.headers },
});

/**
 * Stable "event" callback wrapper:
 * - returns a function with stable identity
 * - always calls the latest version of `fn`
 */
function useEventCallback<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => TResult,
): (...args: TArgs) => TResult {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);
  return useCallback((...args: TArgs) => ref.current(...args), []);
}

const useAuthedRequest = () => {
  const { environment } = useEnvironment();
  const { serviceKey } = useServiceMode();

  const baseOptions = useMemo<BaseRequestOptions>(() => {
    const computedHeaders: Record<string, string> = {};
    if (serviceKey) {
      computedHeaders['x-api-key'] = serviceKey;
    }
    return {
      envId: environment?.envId,
      withoutToken: !!serviceKey,
      headers: computedHeaders,
    };
  }, [environment?.envId, serviceKey]);

  const getFn = useCallback<AuthedRequest['get']>(
    async (url, options) => get(url, buildRequestOptions(baseOptions, options)),
    [baseOptions],
  );

  const postFn = useCallback<AuthedRequest['post']>(
    async (url, body, options) =>
      post(url, body, buildRequestOptions(baseOptions, options)),
    [baseOptions],
  );

  const patchFn = useCallback<AuthedRequest['patch']>(
    async (url, body, options) =>
      patch(url, body, buildRequestOptions(baseOptions, options)),
    [baseOptions],
  );

  const putFn = useCallback<AuthedRequest['put']>(
    async (url, body, options) =>
      put(url, body, buildRequestOptions(baseOptions, options)),
    [baseOptions],
  );

  const removeFn = useCallback<AuthedRequest['remove']>(
    async (url, options) =>
      remove(url, buildRequestOptions(baseOptions, options)),
    [baseOptions],
  );

  // Expose stable outward functions that always call the latest inner implementations.
  const stableGet = useEventCallback(getFn);
  const stablePost = useEventCallback(postFn);
  const stablePatch = useEventCallback(patchFn);
  const stablePut = useEventCallback(putFn);
  const stableRemove = useEventCallback(removeFn);

  return useMemo<AuthedRequest>(
    () => ({
      get: stableGet,
      post: stablePost,
      patch: stablePatch,
      put: stablePut,
      remove: stableRemove,
    }),
    [stableGet, stablePost, stablePatch, stablePut, stableRemove],
  );
};

export default useAuthedRequest;
